// A tiled Recast navmesh over the game's geometry, with Detour's path queries (decision 0049). Every tile is built by
// the staged tile job (tilejob.ts), at load all at once and later one unit at a time: the build and a rebuild are the
// same code. The geometry comes in two kinds: the static level (indexed by tile once, at load) and dynamic shapes by
// id (a crate, a pot, a closed door) that can be switched off and on; a tile is built from the static triangles that
// overlap it plus the dynamic shapes that are on and overlap it, and remembers which ones it was built with, so a save
// can say exactly what each tile holds. `rebuildTile` (phase B's seam) still rebuilds one tile at once from new
// geometry; phase C's slicer (slicer.ts) spreads tile jobs over frames (gate C5). Every measure here is in metres; the
// package converts to Recast's voxel units. Built once at load, after `initNav()` has resolved.
import { NavMesh, NavMeshParams, NavMeshQuery, getNavMeshPositionsAndIndices, type RecastBuildContext } from "recast-navigation";
import { buildTiledNavMeshRcConfig } from "recast-navigation/generators";
import { geometryBounds, navGeometry, type NavGeometry, type NavShape, type NavVec3 } from "./geometry";
import { createTileJob, quietContext, tileBoxes, tileRecastConfig, type TileJob, type TileOutcome, type TileSource } from "./tilejob";

/** How the navmesh is built, in metres and degrees (the game's numbers). */
export interface NavMeshConfig {
  /** Voxel size across (m). Smaller is closer to the geometry and slower. */
  cellSize: number;
  /** Voxel size up (m). */
  cellHeight: number;
  /** Tile edge in voxels (a tile is `tileSize × cellSize` metres square). */
  tileSize: number;
  /** The agent the mesh is eroded for: its radius, height and the step it climbs (m). */
  agentRadius: number;
  agentHeight: number;
  agentClimb: number;
  /** The steepest walkable slope (degrees). */
  maxSlope: number;
  /** Regions smaller than this many voxels across are dropped (default 4). */
  minRegion?: number;
  /** The bounds to tile; default the geometry's. Fix them when tiles must stay put across rebuilds. */
  bounds?: [NavVec3, NavVec3];
}

/** One tile's coordinates in the tile grid. */
export interface TileXY {
  x: number;
  y: number;
}

/** What the build did, in one line's worth of numbers (the "build report" suggestion). */
export interface NavBuildReport {
  tiles: { across: number; down: number; built: number; empty: number; failed: number };
  polys: number;
  triangles: number;
  /** Wall-clock milliseconds the build took (a count of work, not a frame time, under SwiftShader or Node). */
  ms: number;
  bounds: [NavVec3, NavVec3];
}

/** A path query's answer. */
export interface NavPath {
  /** Whether Detour found a path (a partial one counts: it ends at the polygon nearest the goal). */
  ok: boolean;
  /** The straight-path corners, start first, goal (or the nearest reachable point) last. */
  corners: NavVec3[];
  /** The last corner is not the goal: the goal was unreachable or off the mesh. */
  partial: boolean;
}

/** What every tile a dynamic shape could touch was built with: plain data, for saves. */
export interface NavDynamicState {
  /** The dynamic shapes switched on. */
  on: string[];
  /** ["x,y", the dynamic ids the tile was last built with] for every tile some dynamic shape overlaps. */
  tiles: [string, string[]][];
}

/** A staged tile rebuild, with the dynamic shapes it builds in. */
export interface NavTileJob extends TileJob {
  /** The dynamic shape ids this job builds into its tile (sorted). */
  readonly ids: readonly string[];
}

/** A tiled navmesh and its queries. */
export interface NavWorld {
  readonly navMesh: NavMesh;
  readonly query: NavMeshQuery;
  readonly config: Readonly<NavMeshConfig>;
  /** The tile grid's size and each tile's edge in metres. */
  readonly grid: { across: number; down: number; tileMetres: number; origin: NavVec3 };
  /** The tile under (x, z). */
  tileAt(x: number, z: number): TileXY;
  /** The nearest point on the mesh within `extents` (half sizes, m) of `p`, or null. */
  nearest(p: NavVec3, extents?: NavVec3): { point: NavVec3; ref: number } | null;
  /** A path from `from` to `to` (both snapped to the mesh within the query extents). */
  path(from: NavVec3, to: NavVec3): NavPath;
  /** Rebuild one tile at once from new geometry (the whole level's, or what overlaps the tile and its border): phase
   *  B's seam. Returns false when the tile came out empty or failed (it is then removed). For rebuilds spread over
   *  frames, switch a dynamic shape and give its tiles to a `NavTileSlicer`. */
  rebuildTile(tile: TileXY, geometry: NavGeometry): boolean;
  /** Remove one tile (a streamed chunk unloading: phase C1). Agents on it lose their polygon. False when none was there. */
  removeTile(tile: TileXY): boolean;
  /** Whether a tile is built at these coordinates. */
  hasTile(tile: TileXY): boolean;
  /** The built tiles' coordinates, in grid order (row by row). */
  tiles(): TileXY[];
  /** The dynamic shapes' ids, in the order added. */
  dynamicIds(): string[];
  /** Whether a dynamic shape is on (built into its tiles). */
  dynamicOn(id: string): boolean;
  /** Switch a dynamic shape off (a crate broken, a door opened) or on; returns the tiles it overlaps, which need a
   *  rebuild to show it (nothing is rebuilt here). Throws on an unknown id. */
  setDynamic(id: string, on: boolean): TileXY[];
  /** Add a dynamic shape (off until `setDynamic(id, true)` and its tiles rebuilt). Throws if the id is taken. */
  addDynamic(id: string, shapes: readonly NavShape[]): void;
  /** A staged rebuild of one tile from the static geometry plus the dynamic shapes in `ids` (default: those on that
   *  overlap it). Run its units (`unit()`), or hand it to a slicer; the tile in use stays until the last unit swaps. */
  tileJob(tile: TileXY, ids?: readonly string[]): NavTileJob;
  /** The dynamic ids a tile was last built with (sorted). */
  builtWith(tile: TileXY): string[];
  /** Every tile some dynamic shape overlaps, and what each was built with; plus the shapes switched on. */
  dynamicState(): NavDynamicState;
  /** Make the mesh what a `dynamicState()` describes: the switches as saved, and every tile it lists rebuilt at once
   *  (load time) where it was built with other shapes. */
  restoreDynamic(s: NavDynamicState): void;
  /** The mesh's polygons as triangles, for the debug view: xyz per vertex, and the tile each triangle belongs to. */
  debugTriangles(): { positions: number[]; indices: number[] };
  report(): NavBuildReport;
  dispose(): void;
}

/** Default query half extents: generous across, tall enough to find the floor under a hovering agent (m). */
const EXTENTS: NavVec3 = [2, 4, 2];
/** A path ending further than this across the ground from its goal is partial (m). */
const PARTIAL = 0.25;

const v = (p: NavVec3) => ({ x: p[0], y: p[1], z: p[2] });
const t = (p: { x: number; y: number; z: number }): NavVec3 => [p.x, p.y, p.z];
const key = (x: number, y: number): string => `${x},${y}`;

/** How `buildNavWorld` starts. */
export interface NavBuildOptions {
  /** Every tile built from the geometry now (the default), or none (a streamed world builds each tile when its chunk
   *  loads; then `config.bounds` fixes the grid and the geometry may be empty). */
  tiles?: "all" | "none";
  /** Dynamic shapes by id (all on). */
  dynamic?: Record<string, readonly NavShape[]>;
  /** A streamed world's static geometry for one tile (what overlaps it and its border), asked when the tile's job
   *  starts; used in place of `geometry` for that tile. Null: nothing static there. */
  source?: (tile: TileXY) => NavGeometry | null;
}

/** Build a tiled navmesh over the geometry (the static level) and the dynamic shapes by id (all on). Call after
 *  `initNav()` resolves. Throws when Recast can't start the mesh. */
export function buildNavWorld(geometry: NavGeometry, config: NavMeshConfig, o: NavBuildOptions = {}): NavWorld {
  const t0 = performance.now();
  if (o.tiles === "none" && !config.bounds) throw new Error("nav: a navmesh started with no tiles needs fixed bounds");
  const bounds = config.bounds ?? geometryBounds(geometry);
  const cs = config.cellSize;
  const ch = config.cellHeight;
  const recastConfig = {
    cs,
    ch,
    tileSize: config.tileSize,
    walkableSlopeAngle: config.maxSlope,
    walkableHeight: Math.ceil(config.agentHeight / ch),
    walkableClimb: Math.floor(config.agentClimb / ch),
    walkableRadius: Math.ceil(config.agentRadius / cs),
    maxEdgeLen: Math.round(12 / cs),
    maxSimplificationError: 1.3,
    minRegionArea: config.minRegion ?? 4,
    mergeRegionArea: 20,
    maxVertsPerPoly: 6,
    detailSampleDist: 6,
    detailSampleMaxError: 1,
    borderSize: 0,
  };
  const rc = buildTiledNavMeshRcConfig({ recastConfig, navMeshBounds: bounds });
  const cfg = tileRecastConfig(rc.config);
  const navMesh = new NavMesh();
  const params = NavMeshParams.create({ orig: rc.orig, tileWidth: rc.tcs, tileHeight: rc.tcs, maxTiles: rc.maxTiles, maxPolys: rc.maxPolysPerTile });
  if (!navMesh.initTiled(params)) throw new Error("nav: Recast could not start a tiled navmesh");
  const query = new NavMeshQuery(navMesh);
  query.defaultQueryHalfExtents = v(EXTENTS);
  const ctx: RecastBuildContext = quietContext();
  const across = rc.tileWidth;
  const down = rc.tileHeight;
  const border = cfg.borderSize * cs;
  const boxOf = (x: number, y: number) => tileBoxes(bounds[0], bounds[1][1], rc.tcs, border, x, y);

  /** The tiles whose expanded box overlaps an xz rectangle. */
  const tilesOver = (minX: number, minZ: number, maxX: number, maxZ: number): TileXY[] => {
    const x0 = Math.max(0, Math.floor((minX - border - bounds[0][0]) / rc.tcs));
    const x1 = Math.min(across - 1, Math.floor((maxX + border - bounds[0][0]) / rc.tcs));
    const y0 = Math.max(0, Math.floor((minZ - border - bounds[0][2]) / rc.tcs));
    const y1 = Math.min(down - 1, Math.floor((maxZ + border - bounds[0][2]) / rc.tcs));
    const out: TileXY[] = [];
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) out.push({ x, y });
    return out;
  };
  /** Each triangle of a geometry into the tiles its expanded box overlaps. */
  const bucket = (g: NavGeometry, only?: TileXY): Map<string, number[]> => {
    const out = new Map<string, number[]>();
    const P = g.positions;
    const I = g.indices;
    for (let tri = 0; tri < I.length / 3; tri++) {
      let minX = Infinity;
      let minZ = Infinity;
      let maxX = -Infinity;
      let maxZ = -Infinity;
      for (let k = 0; k < 3; k++) {
        const i = I[tri * 3 + k]! * 3;
        const x = P[i]!;
        const z = P[i + 2]!;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (z < minZ) minZ = z;
        if (z > maxZ) maxZ = z;
      }
      for (const tl of tilesOver(minX, minZ, maxX, maxZ)) {
        if (only && (tl.x !== only.x || tl.y !== only.y)) continue;
        const k = key(tl.x, tl.y);
        let list = out.get(k);
        if (!list) out.set(k, (list = []));
        list.push(tri);
      }
    }
    return out;
  };
  const statics = new Map<string, Uint32Array>([...bucket(geometry)].map(([k, l]) => [k, Uint32Array.from(l)]));

  interface Dynamic {
    id: string;
    geometry: NavGeometry;
    tiles: TileXY[];
    on: boolean;
  }
  const dynamics = new Map<string, Dynamic>();
  const addDynamic = (id: string, shapes: readonly NavShape[]): void => {
    if (dynamics.has(id)) throw new Error(`nav: dynamic shape "${id}" exists`);
    const g = navGeometry(shapes);
    const [min, max] = geometryBounds(g);
    dynamics.set(id, { id, geometry: g, tiles: g.indices.length ? tilesOver(min[0], min[2], max[0], max[2]) : [], on: false });
  };
  for (const [id, shapes] of Object.entries(o.dynamic ?? {})) {
    addDynamic(id, shapes);
    dynamics.get(id)!.on = true;
  }
  const overlapping = (tile: TileXY): Dynamic[] => [...dynamics.values()].filter((d) => d.tiles.some((x) => x.x === tile.x && x.y === tile.y));
  const built = new Map<string, string[]>();

  const tileJob = (tile: TileXY, ids?: readonly string[]): NavTileJob => {
    const use = (ids ? ids.map((id) => dynamics.get(id)).filter((d): d is Dynamic => !!d) : overlapping(tile).filter((d) => d.on)).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const sources: TileSource[] = [];
    if (o.source) {
      // a streamed tile: its own geometry, narrowed to the tile and its border
      const g = o.source(tile);
      const tris = g ? bucket(g, tile).get(key(tile.x, tile.y)) : undefined;
      if (g && tris && tris.length) sources.push({ geometry: g, tris });
    } else {
      const st = statics.get(key(tile.x, tile.y));
      if (st && st.length) sources.push({ geometry, tris: st });
    }
    for (const d of use) sources.push({ geometry: d.geometry, tris: allTris(d.geometry) });
    const job = createTileJob({ tile, navMesh, cfg, box: boxOf(tile.x, tile.y), sources, ctx });
    const record = (): void => void built.set(key(tile.x, tile.y), use.map((d) => d.id));
    // record what the tile holds once the job has swapped (or emptied) it
    return {
      tile,
      ids: use.map((d) => d.id),
      get done() {
        return job.done;
      },
      get stage() {
        return job.stage;
      },
      get outcome() {
        return job.outcome;
      },
      unit: () => {
        const fin = job.unit();
        if (fin) record();
        return fin;
      },
      finish: () => {
        const r = job.finish();
        record();
        return r;
      },
      cancel: () => job.cancel(),
    };
  };

  const counts: Record<TileOutcome, number> = { built: 0, empty: 0, failed: 0 };
  if (o.tiles !== "none") for (let y = 0; y < down; y++) for (let x = 0; x < across; x++) counts[tileJob({ x, y }).finish()]++;
  const ms = performance.now() - t0;

  const polyCount = (): number => {
    let n = 0;
    for (let i = 0; i < navMesh.getMaxTiles(); i++) {
      const tile = navMesh.getTile(i);
      const h = tile.header();
      if (h) n += h.polyCount();
    }
    return n;
  };
  const dynamicTiles = (): TileXY[] => {
    const seen = new Map<string, TileXY>();
    for (const d of dynamics.values()) for (const tl of d.tiles) seen.set(key(tl.x, tl.y), tl);
    return [...seen.values()].sort((a, b) => a.y - b.y || a.x - b.x);
  };

  const world: NavWorld = {
    navMesh,
    query,
    config,
    grid: { across, down, tileMetres: rc.tcs, origin: bounds[0] },
    tileAt: (x, z) => ({ x: Math.floor((x - bounds[0][0]) / rc.tcs), y: Math.floor((z - bounds[0][2]) / rc.tcs) }),
    nearest(p, extents = EXTENTS) {
      const r = query.findNearestPoly(v(p), { halfExtents: v(extents) });
      if (!r.success || !r.nearestRef) return null;
      return { point: t(r.nearestPoint), ref: r.nearestRef };
    },
    path(from, to) {
      const r = query.computePath(v(from), v(to));
      if (!r.success || r.path.length === 0) return { ok: false, corners: [], partial: true };
      const corners = r.path.map(t);
      const last = corners[corners.length - 1]!;
      // partial: the path ends away from the goal across the ground (snapping a goal onto the floor below it is not)
      const partial = Math.hypot(last[0] - to[0], last[2] - to[2]) > PARTIAL;
      return { ok: true, corners, partial };
    },
    rebuildTile(tile, g) {
      const tris = bucket(g, tile).get(key(tile.x, tile.y)) ?? [];
      const job = createTileJob({ tile, navMesh, cfg, box: boxOf(tile.x, tile.y), sources: tris.length ? [{ geometry: g, tris }] : [], ctx });
      const r = job.finish();
      built.set(key(tile.x, tile.y), ["(given geometry)"]);
      return r === "built";
    },
    removeTile(tile) {
      built.delete(key(tile.x, tile.y));
      const ref = navMesh.getTileRefAt(tile.x, tile.y, 0);
      if (!ref) return false;
      navMesh.removeTile(ref);
      return true;
    },
    hasTile: (tile) => navMesh.getTileRefAt(tile.x, tile.y, 0) !== 0,
    tiles() {
      const out: TileXY[] = [];
      for (let y = 0; y < down; y++) for (let x = 0; x < across; x++) if (navMesh.getTileRefAt(x, y, 0) !== 0) out.push({ x, y });
      return out;
    },
    dynamicIds: () => [...dynamics.keys()],
    dynamicOn: (id) => {
      const d = dynamics.get(id);
      if (!d) throw new Error(`nav: no dynamic shape "${id}"`);
      return d.on;
    },
    setDynamic(id, on) {
      const d = dynamics.get(id);
      if (!d) throw new Error(`nav: no dynamic shape "${id}"`);
      d.on = on;
      return d.tiles.map((x) => ({ ...x }));
    },
    addDynamic,
    tileJob,
    builtWith: (tile) => [...(built.get(key(tile.x, tile.y)) ?? [])],
    dynamicState: () => ({
      on: [...dynamics.values()].filter((d) => d.on).map((d) => d.id),
      tiles: dynamicTiles().map((tl) => [key(tl.x, tl.y), [...(built.get(key(tl.x, tl.y)) ?? [])]]),
    }),
    restoreDynamic(s) {
      for (const d of dynamics.values()) d.on = s.on.includes(d.id);
      for (const [k, ids] of s.tiles) {
        const [x, y] = k.split(",").map(Number) as [number, number];
        const have = built.get(k) ?? [];
        if (have.length === ids.length && have.every((id, i) => id === ids[i])) continue;
        tileJob({ x, y }, ids).finish();
      }
    },
    debugTriangles() {
      const [positions, indices] = getNavMeshPositionsAndIndices(navMesh);
      return { positions, indices };
    },
    report: () => {
      const [, idx] = getNavMeshPositionsAndIndices(navMesh);
      return { tiles: { across, down, ...counts }, polys: polyCount(), triangles: idx.length / 3, ms, bounds };
    },
    dispose() {
      query.destroy();
      navMesh.destroy();
    },
  };
  return world;
}

const allTrisCache = new WeakMap<NavGeometry, Uint32Array>();
/** Every triangle of a geometry, as an index list (cached). */
function allTris(g: NavGeometry): Uint32Array {
  let a = allTrisCache.get(g);
  if (!a) {
    a = new Uint32Array(g.indices.length / 3);
    for (let i = 0; i < a.length; i++) a[i] = i;
    allTrisCache.set(g, a);
  }
  return a;
}
